import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { MdCheck, MdClose } from 'react-icons/md';
import { AskCarlToolEvent } from '../../types/askCarl';
import CarlThinkingSparkle from './CarlThinkingSparkle';

/**
 * Animated substep timeline for a running/finished Carl turn (ENG-1711).
 * Each tool call renders as a step: pulsing sparkle + shimmer text while
 * running, check when completed, X when failed. Styled for the platform
 * light theme.
 */

const shimmer = keyframes`
    0% { background-position: -200px 0; }
    100% { background-position: 200px 0; }
`;

const stepIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
`;

const ToolStepTimeline = ({ steps }: { steps: AskCarlToolEvent[]; }) => {
    if (!steps.length) return null;

    return (
        <VStack spacing={0} align="stretch" mt={1}>
            {steps.map((step, index) => {
                const status = step.status ?? 'completed';
                const isLast = index === steps.length - 1;
                return (
                    <Flex key={`${step.callId ?? step.toolName}-${index}`} align="flex-start" gap={2} animation={`${stepIn} 0.25s ease-out`}>
                        {/* Rail: status dot + connector */}
                        <Flex direction="column" align="center" pt="3px">
                            {status === 'running' ? (
                                <CarlThinkingSparkle size="14px" />
                            ) : (
                                <Flex
                                    align="center"
                                    justify="center"
                                    w="14px"
                                    h="14px"
                                    borderRadius="full"
                                    bg={status === 'failed' ? 'red.100' : 'green.100'}
                                >
                                    <Icon
                                        as={(status === 'failed' ? MdClose : MdCheck) as React.ElementType}
                                        boxSize={2.5}
                                        color={status === 'failed' ? 'red.500' : 'green.600'}
                                    />
                                </Flex>
                            )}
                            {!isLast && <Box w="1px" flex={1} minH="8px" bg="gray.200" mt="2px" />}
                        </Flex>
                        <HStack spacing={1.5} pb={isLast ? 0 : 1.5} minW={0}>
                            {status === 'running' ? (
                                <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    sx={{
                                        background: 'linear-gradient(90deg, rgba(0,0,0,0.35) 25%, #0071E3 50%, rgba(0,0,0,0.35) 75%)',
                                        backgroundSize: '200px 100%',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        animation: `${shimmer} 1.4s linear infinite`,
                                    }}
                                >
                                    {step.summary}
                                </Text>
                            ) : (
                                <Text fontSize="xs" color={status === 'failed' ? 'red.500' : 'gray.500'}>
                                    {step.summary.replace(/…$/, '')}
                                </Text>
                            )}
                        </HStack>
                    </Flex>
                );
            })}
        </VStack>
    );
};

export default ToolStepTimeline;
